import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const sectionRef = useRef(null);
  const bgRef = useRef(null);
  const fadeLayer1Ref = useRef(null);
  const fadeLayer2Ref = useRef(null);
  const heroLineRef = useRef(null);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const heroText = "Plan Better. Build Smarter. Live Happier";
  const characters = heroText.split("");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.04,
        delayChildren: 0,
      },
    },
  };

  const characterVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const subtextVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
        delay: 1.2,
      },
    },
  };

  useGSAP(
    () => {
      // Trigger animation on page load and scroll
      if (heroLineRef.current) {
        gsap.fromTo(
          heroLineRef.current,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.1,
            scrollTrigger: {
              trigger: heroLineRef.current,
              start: "top 80%",
              toggleActions: "play none none none",
              once: false,
            },
          }
        );
      }

      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.from(".hero-subtext", { y: 28, opacity: 0, duration: 0.8 }, "-=0.35")
        .from(".hero-cta", { scale: 0.78, opacity: 0, duration: 0.7, ease: "back.out(1.7)" }, "-=0.25")
        .from(".hero-panel", { x: 40, opacity: 0, duration: 0.9 }, "-=0.45")
        .from(".hero-stat", { y: 20, opacity: 0, stagger: 0.12, duration: 0.55 }, "-=0.55");

      gsap.to(bgRef.current, {
        yPercent: 12,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.to(bgRef.current, {
        scale: 1.08,
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(fadeLayer1Ref.current, {
        opacity: 0.42,
        duration: 4.8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(fadeLayer2Ref.current, {
        opacity: 0.34,
        duration: 5.6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        delay: 0.8,
      });

      // Trigger Framer Motion animation on load and scroll
      setTimeout(() => setShouldAnimate(true), 0);
    },
    { scope: sectionRef }
  );

  return (
    <section id="hero" ref={sectionRef} className="relative flex min-h-screen items-center overflow-hidden">
      <div
        ref={bgRef}
        className="absolute inset-0 scale-110 bg-cover bg-center"
        style={{ backgroundImage: "url('/img/intro-bg.jpeg')" }}
      /> 
      <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/75 to-navy" />
      <div className="absolute inset-0 bg-hero-gradient opacity-90" />
     

      <div className="section-shell relative z-10 flex flex-col items-center justify-center gap-10 pt-28 ">
       
          <h1 className="mt-8 text-5xl font-extrabold leading-tight text-white text-center sm:text-9xl lg:text-8xl whitespace-nowrap">
            <motion.span
              ref={heroLineRef}
              className="hero-line inline-flex justify-center gap-0"
              variants={containerVariants}
              initial="hidden"
              animate={shouldAnimate ? "visible" : "hidden"}
            >
              {characters.map((char, index) => (
                <motion.span 
                  key={index} 
                  variants={characterVariants}
                  className="inline"
                >
                  {char === " " ? "\u00A0" : char}
                </motion.span>
              ))}
            </motion.span>
          </h1>
          <motion.p 
            className="hero-subtext mt-6 text-xl text-slate-200 text-center sm:text-2xl"
            variants={subtextVariants}
            initial="hidden"
            animate={shouldAnimate ? "visible" : "hidden"}
          >
            We Deliver peace Of Mind.
          </motion.p>
      </div>
    </section>
  );
};

export default Hero;
